import React, { createContext, useContext, useEffect, useState } from 'react';

// ---------- THEME ----------
const ThemeCtx = createContext({ theme: 'dark', setTheme: () => {} });

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('dts_theme')) || 'dark',
  );
  useEffect(() => {
    document.documentElement.classList.remove('theme-dark', 'theme-light');
    document.documentElement.classList.add(`theme-${theme}`);
    localStorage.setItem('dts_theme', theme);
  }, [theme]);
  const setTheme = (t) => setThemeState(t);
  return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
};
export const useTheme = () => useContext(ThemeCtx);

// ---------- LANGUAGE ----------
const DICT = {
  it: {
    'nav.home': 'Home',
    'nav.chi_siamo': 'Chi Siamo',
    'nav.servizi': 'Servizi',
    'nav.formazione': 'Formazione e Corsi',
    'nav.acdt': 'ACDT',
    'nav.energy': 'Energy Power',
    'nav.blog': 'Blog',
    'nav.contatti': 'Contatti',
    'nav.academy': 'Academy',
    'nav.clienti': 'Clienti',
    'ui.theme': 'Tema',
    'ui.language': 'Lingua',
    'ui.dark': 'Scuro',
    'ui.light': 'Chiaro',
    // Footer
    'footer.tagline': 'Un ecosistema che unisce tecnologia, persone e sostenibilita. Costruiamo connessioni digitali che generano valore reale.',
    'footer.navigation': 'navigazione',
    'footer.contact': 'contatti',
    'footer.catalog': 'Catalogo Corsi',
    'footer.login': 'Accedi',
    'footer.acdt_cert': 'Certificazione ACDT',
    'footer.renewals': 'Rinnovi tessera e registro',
    'footer.cta_consult': 'Prenota una consulenza',
    'footer.owner_label': 'Titolare',
    'footer.vat': 'P.IVA / C.F.',
    'footer.hq': 'Sede',
    'footer.l42013_short': 'Formazione erogata ex Legge 14 gennaio 2013, n. 4 (professioni non organizzate), in collaborazione con A.C.D.T. iscritta all elenco MIMIT.',
    'footer.rights': 'Tutti i diritti riservati.',
    'footer.privacy': 'Privacy Policy',
    'footer.cookie_policy': 'Cookie Policy',
    'footer.terms': 'Termini',
    'footer.cookie_prefs': 'Modifica preferenze cookie',
    // Home hero
    'home.kicker': '_le nuove connessioni digitali',
    'home.hero_title_1': 'Trasformiamo il',
    'home.hero_title_highlight': 'digitale',
    'home.hero_title_2': 'in valore reale',
    'home.hero_sub': 'Siti web, consulenza AI Act, formazione certificata ACDT, pubblicita innovativa su totem ChargeBull. Un unico partner per ogni esigenza digitale.',
    'home.cta_primary': 'Scopri i servizi',
    'home.cta_secondary': 'Prenota una consulenza',
    // Common CTAs
    'cta.contact': 'Contattaci',
    'cta.learn_more': 'Scopri di piu',
    'cta.start_now': 'Inizia ora',
    'cta.book_call': 'Prenota una call',
    'cta.buy_now': 'Acquista ora',
  },
  en: {
    'nav.home': 'Home',
    'nav.chi_siamo': 'About Us',
    'nav.servizi': 'Services',
    'nav.formazione': 'Training & Courses',
    'nav.acdt': 'ACDT',
    'nav.energy': 'Energy Power',
    'nav.blog': 'Blog',
    'nav.contatti': 'Contact',
    'nav.academy': 'Academy',
    'nav.clienti': 'Clients',
    'ui.theme': 'Theme',
    'ui.language': 'Language',
    'ui.dark': 'Dark',
    'ui.light': 'Light',
    'footer.tagline': 'An ecosystem that unites technology, people and sustainability. We build digital connections that generate real value.',
    'footer.navigation': 'navigation',
    'footer.contact': 'contact',
    'footer.catalog': 'Course Catalog',
    'footer.login': 'Log in',
    'footer.acdt_cert': 'ACDT Certification',
    'footer.renewals': 'Membership & registry renewals',
    'footer.cta_consult': 'Book a consultation',
    'footer.owner_label': 'Owner',
    'footer.vat': 'VAT / Tax ID',
    'footer.hq': 'Address',
    'footer.l42013_short': 'Training provided pursuant to Italian Law n. 4/2013 on non-regulated professions, in collaboration with A.C.D.T., registered on the MIMIT list.',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.cookie_policy': 'Cookie Policy',
    'footer.terms': 'Terms',
    'footer.cookie_prefs': 'Change cookie preferences',
    'home.kicker': '_new digital connections',
    'home.hero_title_1': 'We turn',
    'home.hero_title_highlight': 'digital',
    'home.hero_title_2': 'into real value',
    'home.hero_sub': 'Websites, AI Act consulting, ACDT certified training, innovative advertising on ChargeBull totems. One partner for every digital need.',
    'home.cta_primary': 'Explore services',
    'home.cta_secondary': 'Book a consultation',
    'cta.contact': 'Contact us',
    'cta.learn_more': 'Learn more',
    'cta.start_now': 'Start now',
    'cta.book_call': 'Book a call',
    'cta.buy_now': 'Buy now',
  },
  es: {
    'nav.home': 'Inicio',
    'nav.chi_siamo': 'Quiénes Somos',
    'nav.servizi': 'Servicios',
    'nav.formazione': 'Formación y Cursos',
    'nav.acdt': 'ACDT',
    'nav.energy': 'Energy Power',
    'nav.blog': 'Blog',
    'nav.contatti': 'Contacto',
    'nav.academy': 'Academy',
    'nav.clienti': 'Clientes',
    'ui.theme': 'Tema',
    'ui.language': 'Idioma',
    'ui.dark': 'Oscuro',
    'ui.light': 'Claro',
    'footer.tagline': 'Un ecosistema que une tecnología, personas y sostenibilidad. Construimos conexiones digitales que generan valor real.',
    'footer.navigation': 'navegación',
    'footer.contact': 'contacto',
    'footer.catalog': 'Catálogo de Cursos',
    'footer.login': 'Iniciar sesión',
    'footer.acdt_cert': 'Certificación ACDT',
    'footer.renewals': 'Renovaciones tarjeta y registro',
    'footer.cta_consult': 'Reserva una consulta',
    'footer.owner_label': 'Titular',
    'footer.vat': 'NIF / CIF',
    'footer.hq': 'Dirección',
    'footer.l42013_short': 'Formación impartida en virtud de la Ley italiana n. 4/2013 sobre profesiones no reguladas, en colaboración con A.C.D.T., inscrita en el listado MIMIT.',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.cookie_policy': 'Política de Cookies',
    'footer.terms': 'Términos',
    'footer.cookie_prefs': 'Cambiar preferencias de cookies',
    'home.kicker': '_nuevas conexiones digitales',
    'home.hero_title_1': 'Convertimos lo',
    'home.hero_title_highlight': 'digital',
    'home.hero_title_2': 'en valor real',
    'home.hero_sub': 'Páginas web, consultoría AI Act, formación certificada ACDT, publicidad innovadora en tótems ChargeBull. Un único partner para toda necesidad digital.',
    'home.cta_primary': 'Descubre los servicios',
    'home.cta_secondary': 'Reserva una consulta',
    'cta.contact': 'Contáctanos',
    'cta.learn_more': 'Saber más',
    'cta.start_now': 'Empieza ahora',
    'cta.book_call': 'Reservar llamada',
    'cta.buy_now': 'Comprar ahora',
  },
};

export const SUPPORTED_LANGS = [
  { code: 'it', label: 'IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
];

const LangCtx = createContext({ lang: 'it', setLang: () => {}, t: (k) => k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(
    () => (typeof window !== 'undefined' && localStorage.getItem('dts_lang')) || 'it',
  );
  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem('dts_lang', lang);
  }, [lang]);
  const setLang = (l) => setLangState(l);
  const t = (key, fallback) => DICT[lang]?.[key] || DICT.it[key] || fallback || key;
  return <LangCtx.Provider value={{ lang, setLang, t }}>{children}</LangCtx.Provider>;
};
export const useLang = () => useContext(LangCtx);
